'use client'

import { useEffect } from 'react'
import Image from 'next/image'
import { useSearchParams } from 'next/navigation'
import { useTranslation } from 'react-i18next'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { BLOCKS, INLINES } from '@contentful/rich-text-types'
import type { Document, Node } from '@contentful/rich-text-types'
import { useFaqDetailDataQuery } from '@/hooks/useContentfulQuery'
import HeaderWithBackButton from '@/components/common/HeaderWithBackButton'
import { GA_EVENTS } from '@/utils/analytics'

type AssetNode = Node & { data?: { target?: { sys?: { id?: string } } } }

type FaqDetailProps = { isModal: boolean, id?: string, isLight?: boolean }

const FaqDetail = ({ isModal, id: idProp, isLight = false }: FaqDetailProps) => {
  const { t } = useTranslation('faq')
  const searchParams = useSearchParams()
  const id = searchParams.get('id') ?? idProp ?? ''

  const { data } = useFaqDetailDataQuery(id)
  const { faq, assets } = data ?? {}

  useEffect(() => {
    GA_EVENTS.FAQ_VIEW(faq?.title ?? id)
  }, [faq])

  if (!faq) return null

  const assetMap = new Map<string, { title?: string; url?: string }>()
  assets?.forEach((asset) => assetMap.set(asset.id, { title: asset.title, url: asset.url }))

  const handleFeedback = (isHelpful: boolean) => {
    GA_EVENTS.BUTTON_CLICK(`faq_feedback_${isHelpful ? 'yes' : 'no'}`)
  }

  const renderOptions = {
    renderNode: {
      [BLOCKS.EMBEDDED_ASSET]: (node: AssetNode) => {
        const id: string | undefined = node?.data?.target?.sys?.id
        const asset = id ? assetMap.get(id) : undefined
        if (!asset || !asset.url) return null
        const src = asset.url.startsWith('https://') ? asset.url : `https:${asset.url}`
        return (
          <div className='rounded-[13px] bg-gray-100 flex items-center justify-center py-[50px]'>
            <Image
              src={src}
              alt={asset.title || 'image'}
              className="max-w-full rounded-md w-[80px] h-[80px]"
              loading="lazy"
              width={500}
              height={500}
            />
          </div>
        )
      },
      [BLOCKS.PARAGRAPH]: (node: Node, children: React.ReactNode) => {
        return (
          <p className={`font-pretendard-regular text-[15px] ${isLight ? 'text-main-light-2' : 'text-text-main-lv2'}`} style={{ whiteSpace: 'pre-line' }}>
            {children}
          </p>
        )
      },
      [INLINES.HYPERLINK]: (node: Node, children: React.ReactNode) => {
        const uri = (node as unknown as { data?: { uri?: string } })?.data?.uri as string
        if (!uri) return children
        return (
          <a href={uri} target="_blank" rel="noopener noreferrer" className="underline text-blue-600 dark:text-blue-400">
            {children}
          </a>
        )
      },
      [BLOCKS.HEADING_1]: (node: Node, children: React.ReactNode) => {
        return (
          <h1 className="font-pretendard-semibold text-[32px] text-main-light-2 dark:text-main-dark-2 leading-tight">
            {children}
          </h1>
        )
      }
    },
  }

  return (
    <section className="sm:mt-5">
      {!isModal && <HeaderWithBackButton />}
      <div className="mx-auto flex flex-col gap-3 px-5 max-w-lg">
        <div className="flex flex-col gap-3">
          <div className='flex gap-[4px]'>
            {faq.category?.map((category) => (
              <p key={category} className={`text-center py-1 px-2 text-primary bg-secondary rounded font-pretendard-semibold text-[11px]`}>
                {t(category)}
              </p>))}
          </div>
          <h2 className={`font-pretendard-semibold text-[19px] ${isLight ? 'text-main-light-2' : 'text-text-main-lv2'} leading-tight`}>
            {faq.title || faq.question}
          </h2>
          {'content' in faq && faq.content && (
            <div className={`flex flex-col font-pretendard-regular text-[15px] ${isLight ? 'text-main-light-2' : 'text-text-main-lv2'} gap-[10px]`}>
              {documentToReactComponents(faq.content as Document, renderOptions)}
            </div>
          )}
        </div>
        <div className="w-full h-[183px] flex flex-col gap-5 justify-center text-center rounded-2xl bg-surface-light mx-auto">
          <p className={`font-pretendard-semibold text-[15px] ${isLight ? 'text-main-light-2' : 'text-text-main-lv2'}`}>
            {t('faq_text_3')}
          </p>
          <div className="flex gap-2 mx-auto">
            <button
              type="button"
              className="w-[123px] py-[11px] rounded-5xl bg-primary text-on-primary text-center"
              onClick={() => handleFeedback(true)}
            >
              {t('yes')}
            </button>
            <button
              type="button"
              className="w-[123px] py-[11px] rounded-5xl bg-label text-on-label text-center"
              onClick={() => handleFeedback(false)}
            >
              {t('no')}
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}

export default FaqDetail